import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar } from 'lucide-react';
import { getAdminAnalytics } from '../../services/api/analytics';
import { format } from 'date-fns';
import { Table } from '../ui/Table';
import { bots } from '../../data/bots';
import { createLogger } from '../../utils/logger';
import type { AdminAnalyticsData } from '../../types/api';

const logger = createLogger('admin-usage-analytics');

const UsageAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<AdminAnalyticsData | null>(null);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      logger.info('[admin-usage-analytics] Fetching admin analytics', {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd')
      });

      const response = await getAdminAnalytics(
        format(dateRange.startDate, 'yyyy-MM-dd'),
        format(dateRange.endDate, 'yyyy-MM-dd')
      );

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch analytics data');
      }

      setAnalyticsData(response.data);
      logger.info('[admin-usage-analytics] Analytics data loaded', {
        totalUsers: response.data.totalUsers,
        totalRevenue: response.data.totalRevenue,
        totalUsage: response.data.totalUsage
      });
    } catch (error) {
      logger.error('[admin-usage-analytics] Failed to fetch analytics data', error);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  if (!analyticsData) {
    return null;
  }

  // Process data for charts
  const usageByBotData = Object.entries(analyticsData.usageByBot).map(([botId, uses]) => {
    const bot = bots.find(b => b.id === botId) || 
               bots.flatMap(b => b.subBots || []).find(sb => sb.id === botId);
    return {
      name: bot?.title || 'Unknown Bot',
      uses,
      revenue: uses * (bot?.price || 0)
    };
  });

  const revenueData = Object.entries(analyticsData.revenueByDay).map(([date, amount]) => ({
    date,
    amount: Number(amount)
  }));

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Usage Analytics</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <input
              type="date"
              value={format(dateRange.startDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                startDate: new Date(e.target.value)
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={format(dateRange.endDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                endDate: new Date(e.target.value)
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-500 mb-2">Total Users</h3>
          <p className="text-3xl font-bold text-gray-900">{analyticsData.totalUsers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-500 mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-gray-900">${analyticsData.totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-500 mb-2">Total Usage</h3>
          <p className="text-3xl font-bold text-gray-900">{analyticsData.totalUsage}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage by Bot</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usageByBotData}>
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="uses" fill="#0891b2" name="Number of Uses" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Generated</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData}>
                <XAxis dataKey="date" tickFormatter={(date) => format(new Date(date), 'MMM dd')} />
                <YAxis />
                <Tooltip 
                  formatter={(value) => `$${Number(value).toFixed(2)}`}
                  labelFormatter={(date) => format(new Date(date), 'PPP')}
                />
                <Bar dataKey="amount" fill="#6366f1" name="Revenue ($)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Details</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uses</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usageByBotData.map((bot) => (
                <tr key={bot.name}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{bot.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{bot.uses}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${bot.revenue.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Total</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{analyticsData.totalUsage}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${analyticsData.totalRevenue.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;